import { getOrganization, searchOrganizations } from "@utils/hermes-api/org";
import {
  Organization,
  SearchOrganizationsResponse,
} from "@xboxstudios/hermes-apis/xboxstudios/hermes/directory/v1/organizations_pb";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { orgKeys } from "./query-keys";
import { gRPCError, ListQueryOptions } from "@utils/hermes-api/types";
import { QueryHookOptions } from "./utils";

export const useOrgQuery = (
  orgName: string,
  queryOptions: QueryHookOptions<Organization, gRPCError> = {}
) => {
  return useQuery({
    queryKey: orgKeys.single(orgName),
    queryFn: () => getOrganization(orgName),
    ...queryOptions,
  });
};

export const useOrgListQuery = (
  queryOptions: QueryHookOptions<SearchOrganizationsResponse, gRPCError> = {},
  filters: ListQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: orgKeys.allLists,
    queryFn: async () => {
      const res = await searchOrganizations(filters);
      for (const o of res.getOrganizationsList()) {
        queryClient.setQueryData(orgKeys.single(o.getName()), o);
      }
      return res;
    },
    select: sortOrgListByName,
    ...queryOptions,
  });
};

export function sortOrgListByName(orgs: SearchOrganizationsResponse) {
  const sortedOrgs = orgs.getOrganizationsList().sort((a, b) => {
    return a.getName().localeCompare(b.getName());
  });
  orgs.setOrganizationsList(sortedOrgs);
  return orgs;
}

export const useOrgMutation = <TVariables>(
  mutateOptions: UseMutationOptions<Organization, gRPCError, TVariables> = {}
) => {
  const queryClient = useQueryClient();
  return useMutation<Organization, gRPCError, TVariables>({
    ...mutateOptions,
    onSuccess: (org, variables, context, meta) => {
      queryClient.setQueryData(orgKeys.single(org.getName()), org);

      // set/update individual cached org
      queryClient.invalidateQueries({
        queryKey: orgKeys.allLists,
        exact: false,
      });

      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(org, variables, context, meta);
      }
    },
  });
};
